import React, {useEffect, useState} from 'react';
import classNames from "classnames";
import {generateTimeSlots} from "../availability-slot-picker";

const Calendar = ({selectedDate, onDateSelect, timeSlots, ...props}) => {
    const today = new Date();
    const [currentMonth, setCurrentMonth] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
    const [unavailableDates, setUnavailableDates] = useState([]);
    const [isCalendarReady, setIsCalendarReady] = useState(false);

    const getDaysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();

    const generateCalendar = () => {
        const month = currentMonth.getMonth();
        const year = currentMonth.getFullYear();
        const daysInMonth = getDaysInMonth(month, year);
        const firstDay = new Date(year, month, 1).getDay();
        const days = [];

        for (let i = 0; i < firstDay; i++) days.push(null);
        for (let d = 1; d <= daysInMonth; d++) days.push(new Date(year, month, d));

        return days;
    };

    const getWorkHoursForDate = (date) => {
        const weekday = date.toLocaleDateString('en-AU', {weekday: 'long'});
        const entry = timeSlots?.find((d) => d.day === weekday);
        if (!entry || entry.status === 'closed') return null;

        const now = new Date();
        if (now.toDateString() === date.toDateString()) {
            const [start, end] = [entry.open, entry.closed];
            const futureSlots = generateTimeSlots(`${start}-${end}`, 30, date);
            return futureSlots.length > 0 ? `${start}-${end}` : null;
        }

        return `${entry.open}-${entry.closed}`;
    };

    useEffect(() => {
        setIsCalendarReady(false);
        const days = generateCalendar();
        const invalids = days.filter(date => {
            if (!date) return false;
            const isPast = date < new Date(today.toDateString());
            const workHours = getWorkHoursForDate(date);
            return isPast || !workHours;
        });
        setUnavailableDates(invalids.map(date => date?.toDateString()));
        setIsCalendarReady(true);
    }, [currentMonth, timeSlots]);

    const isSameDate = (a, b) =>
        a && b && a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();

    const isDisabled = (date) => !isCalendarReady || unavailableDates.includes(date.toDateString());

    const handlePrevMonth = (e) => {
        e.preventDefault();
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
    };

    const handleNextMonth = (e) => {
        e.preventDefault();
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
    };

    const handleResetToToday = (e) => {
        e.preventDefault();
        setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1));
        onDateSelect(today);
    };

    let radius = '0';

    useEffect(() => {
        if (!props.pageRef?.current) return;
        radius = getComputedStyle(props.pageRef.current)?.getPropertyValue('--container-roundness');
    }, [props.pageRef]);

    const days = generateCalendar();

    return (
        <div className={"transition-all duration-300"}>
            <div className="flex justify-between items-center mb-4">
                <div className="no-shrink text-base font-semibold">
                    {currentMonth.toLocaleString('default', {month: 'long'})} {currentMonth.getFullYear()}
                </div>
                <div className="flex no-shrink space-x-2">
                    <div onClick={(e) => {handlePrevMonth(e)}}
                         style={{borderRadius: radius === '0px' ? '0' : ''}}
                         className="flex justify-center items-center cursor-pointer bg-white rounded-theme border border-gray-300 text-sm text-base p-1 w-9 h-9 select-none	">←
                    </div>
                    <div className="flex flex-col items-center">
                        <div
                            onClick={(e) => {handleResetToToday(e)}}
                            style={{borderRadius: radius === '0px' ? '0' : ''}}
                            className="flex justify-center items-center cursor-pointer bg-white rounded-theme border border-gray-300 text-xs p-1 w-9 h-9 select-none	">
                            <div className="w-3	h-3 !bg-base bg-black rounded-full"/>
                        </div>
                    </div>
                    <div onClick={(e) => {handleNextMonth(e)}}
                         style={{borderRadius: radius === '0px' ? '0' : ''}}
                         className="flex justify-center items-center cursor-pointer bg-white rounded-theme border border-gray-300 text-sm text-base p-1 w-9 h-9 select-none">→
                    </div>
                </div>
            </div>
            <div className="grid grid-cols-7 gap-1 text-center text-sm font-semibold text-gray-600 mb-1">
                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => <div key={d}>{d}</div>)}
            </div>

            <div className="grid grid-cols-7 gap-1 text-center text-sm font-medium">
                {days.map((day, i) =>
                    day ? (
                        <button
                            key={i}
                            style={{borderRadius: radius === '0px' ? '0' : ''}}
                            onClick={(e) => {
                                e.preventDefault();
                                !isDisabled(day) && onDateSelect(day)
                            }}
                            disabled={isDisabled(day)}
                            className={classNames(
                                "p-1 rounded-theme transition-all duration-150 border",
                                isDisabled(day)
                                    ? 'text-gray-400 bg-gray-200 cursor-not-allowed border-gray-200'
                                    : isSameDate(day, selectedDate)
                                        ? 'bg-light border-base'
                                        : 'hover:border-base'
                            )}
                        >
                            {day.getDate()}
                        </button>
                    ) : (
                        <div key={i}></div>
                    )
                )}
            </div>
        </div>
    );
};

export default Calendar;
